import { useEffect, useState, CSSProperties, RefObject } from 'react';
import { ImageCropData } from '@repo/types';

/**
 * Pixel-space geometry of a crop region, measured from the live DOM.
 *
 * All values are in the image's intrinsic (decoded) pixel space except `cw`/`ch`,
 * which are the container's rendered dimensions. Crop coordinates are already
 * normalised to the decoded image resolution (see `useCropGeometry`).
 */
export interface CropGeometry {
  /** Decoded image width (`img.naturalWidth`). */
  nw: number;
  /** Decoded image height (`img.naturalHeight`). */
  nh: number;
  /** Crop x offset, normalised to the decoded resolution. */
  x: number;
  /** Crop y offset, normalised to the decoded resolution. */
  y: number;
  /** Crop width, normalised to the decoded resolution. */
  crw: number;
  /** Crop height, normalised to the decoded resolution. */
  crh: number;
  /** Container client width. */
  cw: number;
  /** Container client height. */
  ch: number;
}

export interface BuildCropStyleOptions {
  /** `contain` fits the whole crop region inside the container; `cover` fills it. */
  fit: 'contain' | 'cover';
  /** Clip the image to the crop region via `clip-path: inset(...)`. */
  clip?: boolean;
  /** Optional `position` value to add to the returned style. */
  position?: CSSProperties['position'];
}

/**
 * Builds the inline style that scales and positions an `<img>` so the crop
 * region is centred inside its container. `contain` is used for the visible
 * crop, `cover` for a background fill (e.g. a blurred backdrop).
 */
export function buildCropStyle(geometry: CropGeometry, opts: BuildCropStyleOptions): CSSProperties {
  const { nw, nh, x, y, crw, crh, cw, ch } = geometry;
  const scale = opts.fit === 'cover' ? Math.max(cw / crw, ch / crh) : Math.min(cw / crw, ch / crh);

  const imgW = nw * scale;
  const imgH = nh * scale;
  const cropW = crw * scale;
  const cropH = crh * scale;

  const style: CSSProperties = {
    objectFit: 'fill',
    width: `${imgW}px`,
    height: `${imgH}px`,
    left: `${-(x * scale) + (cw - cropW) / 2}px`,
    top: `${-(y * scale) + (ch - cropH) / 2}px`,
    maxWidth: 'none',
    maxHeight: 'none',
  };

  if (opts.position) {
    style.position = opts.position;
  }

  if (opts.clip) {
    const cx = x * scale;
    const cy = y * scale;
    style.clipPath = `inset(${cy}px ${imgW - cx - cropW}px ${imgH - cy - cropH}px ${cx}px)`;
  }

  return style;
}

/**
 * Measures the crop geometry of an image inside a container using real DOM
 * dimensions (`img.naturalWidth/naturalHeight` + `wrapper.clientWidth/Height`),
 * recomputing on image load and on container resize.
 *
 * This is the approach to use for containers with a **fixed aspect ratio that
 * differs from the crop** (e.g. a 16:9 hero card), where the percentage-based
 * math of `useThumbnailCrop` produces warped offsets. Feed the returned geometry
 * to `buildCropStyle` to derive the `<img>` style. Returns `undefined` until the
 * image has decoded and the container has non-zero dimensions.
 *
 * @param wrapperRef    – ref to the `overflow: hidden` container element
 * @param imageSelector – CSS selector for the `<img>` inside the container
 * @param crop          – crop region from the article (no-op when absent)
 */
export function useCropGeometry<T extends HTMLElement>(
  wrapperRef: RefObject<T | null>,
  imageSelector: string,
  crop?: ImageCropData
): CropGeometry | undefined {
  const [geometry, setGeometry] = useState<CropGeometry>();

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!crop || !wrapper) return;

    const img = wrapper.querySelector(imageSelector) as HTMLImageElement | null;
    if (!img) return;

    const compute = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;

      const cw = wrapper.clientWidth;
      const ch = wrapper.clientHeight;
      if (!cw || !ch) return;

      const { naturalWidth: nw, naturalHeight: nh } = img;

      // Normalise crop coordinates if they were created on a different resolution image
      let { x, y, width: crw, height: crh } = crop;
      if (crop.sourceWidth && crop.sourceHeight && crop.sourceWidth !== nw) {
        const sx = nw / crop.sourceWidth;
        const sy = nh / crop.sourceHeight;
        x *= sx;
        y *= sy;
        crw *= sx;
        crh *= sy;
      }

      if (!crw || !crh) return;

      setGeometry({ nw, nh, x, y, crw, crh, cw, ch });
    };

    // Always observe container resizes so the crop stays accurate after
    // viewport/orientation changes (e.g. a scrollbar altering clientWidth).
    const ro = new ResizeObserver(compute);
    ro.observe(wrapper);

    // Recompute once the image finishes decoding if it hasn't already.
    if (!(img.complete && img.naturalWidth)) {
      img.addEventListener('load', compute);
      return () => {
        img.removeEventListener('load', compute);
        ro.disconnect();
      };
    }

    // Image already decoded — measure immediately.
    compute();
    return () => ro.disconnect();
  }, [crop, imageSelector, wrapperRef]);

  return geometry;
}
